import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { getActiveUser } from 'services/user';
import FBOButton from 'ui/theme/components/FBOButton/FBOButton';
import FBOMenuButton from 'ui/theme/components/FBOMenuButton/FBOMenuButton';
import {
  Undo,
  Redo,
  Grid,
  Eraser,
  Square,
  RectangleHorizontal,
} from 'lucide-react';
import { LabelElement, PageSize, TemplateApiPayload } from '../../types';
import { colorPalette } from 'ui/theme';
import { IconNames } from 'ui/theme';
import { MenuItemVariant } from 'ui/components/Menu';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';
import { MenuItem } from 'ui/components/Menu';
import SliderToggle from 'ui/components/SliderToggle';
import { useSelector } from 'react-redux';
import { HEADER_CONTROLS_HEIGHT } from '../../consts';

interface GenerateTemplateMenuItemsParams {
  isExecutorUser: boolean;
  currentTemplateId?: string | null;
  isCurrentTemplateDefault?: boolean;
  isCurrentTemplateSystem?: boolean;
  onSetAsDefault?: (templateId: string) => void;
  onToggleSystemSetting?: () => void;
  onDeleteTemplate?: (templateId: string) => void;
}

const modeOptions: {
  value: 'design' | 'preview';
  label: string;
  'data-qa': string;
}[] = [
  {
    value: 'design',
    label: 'Design',
    'data-qa': 'slider-toggle-design-mode',
  },
  {
    value: 'preview',
    label: 'Preview',
    'data-qa': 'slider-toggle-preview-mode',
  },
];

enum MenuItemId {
  SET_DEFAULT = 1,
  TOGGLE_SYSTEM = 2,
  DELETE_TEMPLATE = 3,
}

const generateTemplateMenuItems = ({
  isExecutorUser,
  currentTemplateId,
  isCurrentTemplateDefault,
  isCurrentTemplateSystem,
  onSetAsDefault,
  onToggleSystemSetting,
  onDeleteTemplate,
}: GenerateTemplateMenuItemsParams): MenuItem[] => {
  const menuItems = [
    {
      id: MenuItemId.SET_DEFAULT,
      label: 'Set as Default',
      onOptionClick: () => {
        if (currentTemplateId && onSetAsDefault) {
          onSetAsDefault(currentTemplateId);
        }
      },
      disabled:
        !currentTemplateId ||
        isCurrentTemplateDefault ||
        isCurrentTemplateSystem,
      dataQa: 'template-menu-set-default',
    },
    {
      id: MenuItemId.TOGGLE_SYSTEM,
      label: isCurrentTemplateSystem ? 'Unset as System' : 'Set as System',
      onOptionClick: () => {
        if (currentTemplateId && onToggleSystemSetting) {
          onToggleSystemSetting();
        }
      },
      disabled: !currentTemplateId || isCurrentTemplateDefault,
      dataQa: 'template-menu-toggle-system',
    },
    {
      id: MenuItemId.DELETE_TEMPLATE,
      label: 'Delete Template',
      onOptionClick: () => {
        if (currentTemplateId && onDeleteTemplate) {
          onDeleteTemplate(currentTemplateId);
        }
      },
      disabled: !currentTemplateId || isCurrentTemplateDefault,
      dataQa: 'template-menu-delete',
      icon: IconNames.FBODeleteSmall,
      variant: MenuItemVariant.Danger,
    },
  ];

  if (!isExecutorUser && isCurrentTemplateSystem) {
    return [];
  }

  if (!isExecutorUser) {
    return menuItems.filter((item) => item.id !== MenuItemId.TOGGLE_SYSTEM);
  }

  return menuItems;
};

const getTemplateOptionLabel = (option: any): string => {
  let name = `${option.name || 'Unnamed Template'}`;
  if (option.systemTemplate) {
    name += ' (system)';
  }
  if (option.defaultTemplate) {
    name += ' (default)';
  }

  return name;
};

interface DesignerHeaderProps {
  onDeleteElement?: () => void;
  previewMode: boolean;
  onTogglePreview: () => void;
  showGrid: boolean;
  onToggleGrid: () => void;
  showMargins: boolean;
  onToggleMargins: () => void;
  footer: boolean;
  onToggleFooter: () => void;
  selectedPageSize: PageSize;
  onPageSizeChange: (pageSize: PageSize) => void;
  pageSizes: PageSize[];
  onUndo?: () => void;
  onRedo?: () => void;
  onClearCanvas: () => void;
  onGroup: () => void;
  selectedElements: LabelElement[];
  availableTemplates: TemplateApiPayload[];
  onTemplateSelect: (selectedTemplate: TemplateApiPayload | null) => void;
  onCreateNewTemplate: () => void;
  currentTemplateId?: string | null;
  onUpdateTemplate: () => void;
  isUpdatingTemplate?: boolean;
  onDeleteTemplate?: (templateId: string) => void;
  onSetAsDefault?: (templateId: string) => void;
  onToggleSystemSetting?: () => void;
  isCurrentTemplateDefault?: boolean;
  isCurrentTemplateSystem?: boolean;
}

const DesignerHeader: React.FC<DesignerHeaderProps> = ({
  previewMode,
  onTogglePreview,
  showGrid,
  onToggleGrid,
  showMargins,
  onToggleMargins,
  footer,
  onToggleFooter,
  onUndo,
  onRedo,
  onClearCanvas,
  onGroup,
  selectedElements,
  availableTemplates,
  onTemplateSelect,
  onCreateNewTemplate,
  currentTemplateId,
  onUpdateTemplate,
  isUpdatingTemplate = false,
  onDeleteTemplate,
  onSetAsDefault,
  onToggleSystemSetting,
  isCurrentTemplateDefault,
  isCurrentTemplateSystem,
}) => {
  const [clearDialogOpen, setClearDialogOpen] = useState(false);

  const isExecutorUser = useSelector(getActiveUser).isExecutor;

  const handleClearCanvas = () => {
    setClearDialogOpen(false);
    onClearCanvas();
  };

  return (
    <>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Typography variant="h4">Template Designer</Typography>
            <Box sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
              {!previewMode && (
                <>
                  <Tooltip title="Undo (⌘Z)">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ mx: 0.5 }}
                        onClick={onUndo}
                        disabled={!onUndo}
                      >
                        <Undo size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Redo (⌘⇧Z)">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ mx: 0.5 }}
                        onClick={onRedo}
                        disabled={!onRedo}
                      >
                        <Redo size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}
              <Tooltip title={showGrid ? 'Hide Grid' : 'Show Grid'}>
                <IconButton
                  size="small"
                  sx={{
                    mx: 0.5,
                    color: showGrid
                      ? colorPalette.redesign.lightPrimaryBlue
                      : 'inherit',
                  }}
                  onClick={onToggleGrid}
                >
                  <Grid size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  showMargins ? 'Hide Print Margins' : 'Show Print Margins'
                }
              >
                <IconButton
                  size="small"
                  sx={{
                    mx: 0.5,
                    color: showMargins
                      ? '#ff9800' // Amber color to match margin guides
                      : 'inherit',
                  }}
                  onClick={onToggleMargins}
                >
                  <Square size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  footer ? 'Remove Summary Section' : 'Add Summary Section'
                }
              >
                <IconButton
                  size="small"
                  sx={{
                    mx: 0.5,
                    color: footer ? 'primary.main' : 'inherit',
                  }}
                  onClick={onToggleFooter}
                  aria-label={
                    footer ? 'Remove Summary Section' : 'Add Summary Section'
                  }
                  data-qa="footer-toggle"
                >
                  <RectangleHorizontal size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear Canvas">
                <IconButton
                  size="small"
                  sx={{ mx: 0.5 }}
                  onClick={() => setClearDialogOpen(true)}
                >
                  <Eraser size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Center Section - Design/Preview Toggle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <SliderToggle
              options={modeOptions}
              value={previewMode ? 'preview' : 'design'}
              onChange={(event, newValue) => {
                const wantsPreviewMode = newValue === 'preview';
                if (wantsPreviewMode !== previewMode) {
                  onTogglePreview();
                }
              }}
              data-qa="mode-slider-toggle"
              aria-label="View Mode"
              // size="small" // Adjust size as needed: 'small', 'medium', 'large'
            />
          </Box>

          {/* Right Section */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flex: 1,
            }}
          >
            {!previewMode ? (
              <>
                {selectedElements.length > 1 && (
                  <FBOButton
                    variant="secondary"
                    color="positive"
                    icon="group"
                    onClick={onGroup}
                    sx={{ ml: 1 }}
                    data-qa="group-button"
                  >
                    Group Selected Items
                  </FBOButton>
                )}

                <FBOButton
                  icon="plus"
                  variant="primary"
                  color="positive"
                  sx={{ mx: 2 }}
                  onClick={onCreateNewTemplate}
                  data-qa="designer-header-create-new-template-button"
                >
                  New
                </FBOButton>
                {(isExecutorUser || !isCurrentTemplateSystem) && (
                  <FBOButton
                    variant="secondary"
                    color="positive"
                    sx={{ mr: 2 }}
                    onClick={onUpdateTemplate}
                    disabled={!currentTemplateId || isUpdatingTemplate}
                    loading={isUpdatingTemplate}
                    data-qa="designer-header-update-template-button"
                  >
                    Update
                  </FBOButton>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '300px',
                  }}
                >
                  <Autocomplete
                    options={availableTemplates || []}
                    getOptionLabel={getTemplateOptionLabel}
                    value={
                      availableTemplates.find(
                        (template) => template.id === currentTemplateId
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      onTemplateSelect(newValue);
                    }}
                    sx={{ width: '300px' }}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    disableClearable
                    autoSelect={false}
                  />
                </Box>
                {(isExecutorUser || !isCurrentTemplateSystem) && (
                  <FBOMenuButton
                    variant="secondary"
                    items={generateTemplateMenuItems({
                      isExecutorUser,
                      currentTemplateId,
                      isCurrentTemplateDefault,
                      isCurrentTemplateSystem,
                      onSetAsDefault,
                      onToggleSystemSetting,
                      onDeleteTemplate,
                    })}
                    disabled={!currentTemplateId}
                    data-qa="template-options-menu"
                    sx={{
                      color: colorPalette.redesign.contentSecondary,
                      ml: 1,
                    }}
                  />
                )}
              </>
            ) : (
              <Box sx={{ height: HEADER_CONTROLS_HEIGHT }} />
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        aria-labelledby="clear-canvas-dialog-title"
      >
        <DialogTitle id="clear-canvas-dialog-title">Clear Canvas</DialogTitle>
        <DialogContent>
          Are you sure you want to clear the canvas? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <FBOButton
            variant="tertiary"
            color="neutral"
            onClick={() => setClearDialogOpen(false)}
            data-qa="cancel-clear-button"
          >
            Cancel
          </FBOButton>
          <FBOButton
            variant="primary"
            color="negative"
            onClick={handleClearCanvas}
            data-qa="confirm-clear-button"
          >
            Clear Canvas
          </FBOButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DesignerHeader;
